import React, { ReactElement, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { TransactionsResponse } from "../../models/Response";
import { IndexerService } from "../../services/indexer.service";
import { Group, Loader, Skeleton, Stack, Text, Tooltip } from "@mantine/core";
import { IconHelpSquareRounded } from "@tabler/icons-react";
import CopyTextComponent from "../../components/copyText/CopyText.component";
import styles from "./AddressDetailsPage.module.scss";
import { formatAddress, formatAmountWithLabel, formatNumber } from "../../utils/formatter";
import { AddressPair } from "../../models/AddressPair";
import { formatDate } from "../../utils/formatTime";
import { Balances, Stake } from "../../models/Balances";
import { LabelValueStack } from "../../components/labelValueStack/LabelValueStack.component";
import ExportIcsFile from "../../components/exportIcsFile/ExportIcsFile.component";
import Button from "../../components/Button/Button.component";
import GoogleCalendarIcon from "../../components/GoogleCalendarIcon/GoogleCalendarIcon";
import { useStore } from "@tanstack/react-store";
import { IStore, store } from "../../services/store.service";
import AddressDetailsTabs from "../../components/addressDetailsTabs/AddressDetailsTabs.component";

export default function AddressDetailsPage(): ReactElement | null {
  const timeZone = useStore<IStore, IStore["timeZone"]>(store, (state) => state.timeZone).selected;

  const { address } = useParams();
  const navigate = useNavigate();

  const { data: balances, isLoading: isBalancesLoading } = useQuery({
    queryKey: [QueryKey.balances, address],
    queryFn: (): Promise<Balances> =>
      IndexerService.getAddressBalances(address!),
    enabled: !!address,
  });

  const { data: transactionsData, isLoading: transactionLoading } = useQuery({
    queryKey: [QueryKey.transactionList, address, 0, 10, null, []],
    queryFn: (): Promise<TransactionsResponse> =>
      IndexerService.getTransactionsPerAddress(address!, null, null, 0, 10),
    enabled: !!address,
    staleTime: 5000,
  });

  const { data: addressPair, isLoading: ethAddressLoading } = useQuery({
    queryKey: [QueryKey.ethAddress, address],
    queryFn: async (): Promise<AddressPair | null> => {
      const pair: AddressPair | null = await IndexerService.getAddressPair(
        address!,
      );
      return pair;
    },
    enabled: !!address,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  useEffect(() => {
    if (addressPair?.address === address) {
      //url contains eth address instead p-chain address => redirect to P-chain address
      navigate(`/address/${addressPair?.p_address}`, { replace: true });
    }
  }, [address, addressPair]);

  const goHome = () => {
    navigate("/");
  };


  /**
   * Checks if the given value is a valid P-Chain address by verifying if it starts with "flare".
   *
   * @param {string | undefined} value - The string to be checked as a potential P-Chain address.
   * @return {boolean} Returns true if the value starts with "flare", otherwise returns false.
   */
  function isPChainAddress(value: string | undefined): boolean {
    if (!value) {
      return false;
    }

    switch (process.env.REACT_APP_CHAIN?.toLowerCase()) {
      case "flr":
        return value.startsWith("flare");
      case "c2flr":
        return value.startsWith("costwo");
      case "coston":
        return value.startsWith("coston");
      default:
        return value.startsWith("flare");
    }
  }

  return (
    <>
      <div className={styles.txContainer}>
        <div className={styles.txHeader}>
          <h2 className={styles.pageTitle}>Address details</h2>
        </div>
        {isBalancesLoading ? (
          <Loader size={50} style={{ paddingTop: "20%" }} />

          // If it is empty string we cant find the address in our system => display error
          // If there is an error addressPair return null and we display other available data if exists.
        ) : ((addressPair && addressPair.p_address !== "") || addressPair === null) && isPChainAddress(address) ? (
          <Stack align={"start"} gap={0}>
            {/*P-CHAIN ADDRESS*/}
            {isPChainAddress(address) && (
              <Group className={styles.detailRow}>
                <div className={styles.labelContainer}>
                  <div className={styles.textWithHelp}>
                    <Tooltip
                      label={"P-Chain address in Bech32 format"}
                      position="top"
                      events={{ hover: true, focus: false, touch: true }}
                    >
                      <IconHelpSquareRounded size={20} />
                    </Tooltip>

                    <Text ta={{ sm: "center", md: "left" }} fw={700}>
                      P-Chain address:
                    </Text>
                  </div>
                </div>
                {/*Actual value */}
                <div className={styles.valueContainer}>
                  <div
                    className={`${styles.wrapBackground} ${styles.gradientBackground}`}
                  >
                    <CopyTextComponent
                      text={formatAddress(address!, 24, true)}
                      value={address!}
                      weight={"bold"}
                    ></CopyTextComponent>
                    <div
                      className={`${styles.verticalLine} ${styles.hideOnMobile}`}
                    ></div>

                    {transactionLoading ? (
                      <>
                        <Skeleton />
                      </>
                    ) : (
                      <div className={styles.hideOnMobile}>
                        <LabelValueStack
                          label={"Number of transactions"}
                          value={transactionsData?.data.count ?? ""}
                          primary={false}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Group>
            )}


            {/*LINKED C-CHAIN ADDRESS*/}
            {addressPair && (
              <Group className={styles.detailRow}>
                <div className={styles.labelContainer}>
                  <div className={styles.textWithHelp}>
                    <Tooltip
                      label={"C-Chain address that belong to P-Chain address"}
                      position="top"
                      events={{ hover: true, focus: false, touch: true }}
                    >
                      <IconHelpSquareRounded size={20} />
                    </Tooltip>

                    <Text ta={{ sm: "center", md: "left" }} fw={700}>
                      Linked C-Chain address:
                    </Text>
                  </div>
                </div>
                {/*Actual value */}
                <div className={styles.valueContainer}>
                  <div className={styles.gradientBackground}>
                    <CopyTextComponent
                      weight={"bold"}
                      primaryColor={false}
                      link={
                        "https://flare-explorer.flare.network/address/" +
                        addressPair.address
                      }
                      text={formatAddress(addressPair.address, 24, true)}
                      value={addressPair.address}
                    ></CopyTextComponent>
                  </div>
                </div>
              </Group>
            )}

            {/*BALANCE ON ADDRESS*/}
            {balances && (
              <Group className={styles.detailRow}>
                <div className={styles.labelContainer}>
                  <div className={styles.textWithHelp}>
                    <Tooltip
                      label={
                        "Showing total, available and staked balance for P-Chain address"
                      }
                      multiline
                      w={220}
                      events={{ hover: true, focus: false, touch: true }}
                    >
                      <IconHelpSquareRounded size={20} />
                    </Tooltip>

                    <Text ta={{ sm: "center", md: "left" }} fw={700}>
                      Balance:
                    </Text>
                  </div>
                </div>
                {/*Actual value */}
                <div className={styles.valueContainer}>
                  <div className={`${styles.gradientBackground}`}>
                    <LabelValueStack
                      label={"Total balance"}
                      value={formatAmountWithLabel(
                        balances?.total_balance ?? 0,
                        true,
                        2,
                        true,
                      )}
                      primary={false}
                    />
                    <div className={styles.verticalLine}></div>

                    <LabelValueStack
                      label={"Available"}
                      value={formatAmountWithLabel(
                        balances?.available_balance ?? 0,
                        true,
                        2,
                        true,
                      )}
                      primary={false}
                    />
                  </div>
                </div>
              </Group>
            )}


            {/*BALANCE ON ADDRESS - STAKED*/}
            {balances && (
              <Group className={styles.detailRow}>
                <div className={styles.labelContainer}>
                  <div className={styles.textWithHelp}>{/*Empty line*/}</div>
                </div>
                {/*Actual value */}
                <div className={styles.valueContainer}>
                  <div className={styles.gradientBackground}>
                    <LabelValueStack
                      label={"Staked"}
                      value={formatAmountWithLabel(
                        balances?.staked_balance ?? 0,
                        true,
                        2,
                        true,
                      )}
                      primary={false}
                    />
                  </div>
                </div>
              </Group>
            )}

            {/*CURRENTLY STAKED*/}
            {balances?.stakings && balances.stakings.length > 0 && (
              <Group
                className={`${styles.detailRow} ${styles.multilineItem}`}
              >
                <div className={styles.labelContainer}>
                  <div className={styles.textWithHelp}>
                    <Tooltip
                      label={"List of current delegations"}
                      position="top"
                      events={{ hover: true, focus: false, touch: true }}
                    >
                      <IconHelpSquareRounded size={20} />
                    </Tooltip>

                    <Text ta={{ sm: "center", md: "left" }} fw={700}>
                      Currently staked:
                    </Text>
                  </div>
                </div>
                {/*Actual value */}
                <div
                  className={`${styles.valueContainer} ${styles.stakeTable}`}
                >
                  <div
                    className={styles.gradientBackground}
                    style={{ height: "auto" }}
                  >
                    <Stack w={"100%"} gap={8}>
                      <Group className={styles.stakedRow}>
                        <div>
                              <span className={styles.topLabel}>
                                Transaction
                              </span>
                        </div>
                        <div>
                          <span className={styles.topLabel}>Node ID</span>
                        </div>
                        <div className={styles.alignRight}>
                          <span className={styles.topLabel}>Amount</span>
                        </div>
                        {" "}
                        <div>
                          <span className={styles.topLabel}>End time</span>
                        </div>
                      </Group>

                      {balances.stakings.map((stake: Stake) => (
                        <Group
                          className={styles.stakedRow}
                          key={stake.tx_id}
                        >
                          <div>
                            <CopyTextComponent
                              link={`/tx/${stake.tx_id}`}
                              weight={"bold"}
                              text={formatAddress(stake.tx_id, 10, false)}
                              value={stake.tx_id}
                            />
                          </div>
                          <div>
                            <CopyTextComponent
                              weight={"600"}
                              link={`/validator/${stake.node_id}`}
                              text={formatAddress(stake.node_id, 10, false)}
                              value={stake.node_id}
                              primaryColor={false}
                            />
                          </div>
                          <div className={styles.alignRight}>
                                <span>
                                  {formatAmountWithLabel(
                                    stake.amount,
                                    true,
                                    2,
                                    true,
                                  )}
                                </span>
                          </div>
                          {" "}
                          <div>
                                <span className={styles.dateColumn}>
                                  {
                                    formatDate(new Date(stake.end_time * 1000), timeZone)
                                      .formattedDateTime
                                  }
                                  &nbsp;
                                  <span className={styles.unitLabel}>
                                    {
                                      formatDate(
                                        new Date(stake.end_time * 1000), timeZone,
                                      ).utcOffset
                                    }
                                  </span>
                                </span>
                            <ExportIcsFile data={stake} />
                            <GoogleCalendarIcon
                              date={new Date(stake.end_time * 1000)}
                              title={"Flare Staking Ends"}
                              description={`${formatNumber(stake.amount)} FLR staked to \r\n ${stake.node_id}`}
                            />
                          </div>
                        </Group>
                      ))}
                    </Stack>
                  </div>
                </div>
              </Group>
            )}

            {/* ****TABS*****/}
            <Group
              className={`${styles.detailRow} ${styles.multilineItem}`}
              mt={"lg"}
            >
              <div className={styles.labelContainer}>
                <div className={styles.textWithHelp}>
                  <Tooltip
                    label={
                      "List of transaction on this address for all three categories (P-Chain - transactions on p-chain, Cross-Chain - pending cross-chain transactions when moving assets from p-chain to c-chain or vice versa, Unspent outputs - lisf of unspent outputs"
                    }
                    multiline
                    w={220}
                    position="top"
                    events={{ hover: true, focus: false, touch: true }}
                  >
                    <IconHelpSquareRounded size={20} />
                  </Tooltip>

                  <Text ta={{ sm: "center", md: "left" }} fw={700}>
                    Transactions:
                  </Text>
                </div>
              </div>
              {/*Actual value */}
              <div className={`${styles.valueContainer} ${styles.tabelContainerHeight}`}>
                {transactionLoading && <Loader size={20} style={{ paddingTop: "20px" }} />}
                {transactionsData && <AddressDetailsTabs />}
              </div>
            </Group>
          </Stack>
        ) : (
          //TODO: add as standalone component
          <div className={styles.errorLabel}>
            <span>
              Apologies, but we couldn&#39;t locate any activities on the
            </span>
            <span>Flare P-Chain for the provided address.</span>
            <div>
              <Button addStyles={["invert"]} onClick={goHome}>
                View latest blocks
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
